// Channel-level credential form. Channel = marketplace partner
// (Jobalots, BargainFox). One base URL + two HMAC secrets shared
// across all vendor sub-accounts on this channel, plus the master
// enabled toggle at the top of the form. The per-vendor controls
// (vendor_id string + per-vendor enabled toggle) live in
// VendorSubAccountRow rendered below this form by ChannelCard.
//
// LAYOUT. The form grew a field at a time until it was one flat column of
// a dozen unrelated controls — push mode next to AI packaging next to a
// sender address — and nothing signalled which of them belonged together.
// It is now four collapsed sections, each with a one-line summary of its
// own state in the header so the common case (checking a setting) needs no
// clicks at all:
//
//   API integration   everything about talking to the partner's API —
//                     push mode, base URL, both secrets, health, the test
//                     buttons and the bulk stock push. Grouped because
//                     they are one subject and are useless apart.
//   Customer emails   sender identity, logo, and who Portal emails.
//   Order consolidation
//   Extra protection
//
// Only the master enabled toggle stays outside a section: it governs all
// four, and hiding a channel's on/off switch behind a chevron would be
// perverse.
//
// `disabled` is passed for coming-soon channels — every input and
// button is locked, no PATCH ever fires.

import { useCallback, useEffect, useState } from "react";
import { Loader2, Wifi, WifiOff, Mail, X, Plus } from "lucide-react";
import { useTranslation } from "../../../i18n";
import { apiFetch } from "../../../api/apiFetch";
import SecretField from "./SecretField";
import type { ChannelPatch, ConsolidationStats, SalesChannelConfig, TestConnectionResult, TestEmailLanguage, TestEmailScenario } from "./types";
import { TEST_EMAIL_LANGUAGES, TEST_EMAIL_SCENARIOS } from "./types";

interface Props {
    channel: SalesChannelConfig;
    onUpdated: (next: SalesChannelConfig) => void;
    disabled?: boolean;
    // Portal warehouses, for the per-warehouse pallet-consolidation
    // chips. Loaded once by ChannelCard (shared with the vendor rows);
    // empty until it arrives, so the chips simply render once it does.
    warehouses?: { warehouse: string; country?: string | null }[];
}

interface SectionProps {
    title: string;
    // One line of current state, shown in the collapsed header. The point
    // of the sections is fewer clicks, not more: an admin checking whether
    // a channel is live should read it here, not have to open the panel.
    summary?: string;
    children: React.ReactNode;
}

// A collapsed-by-default panel inside a channel card. Native <details>
// rather than useState, for the same reasons ChannelCard itself uses one:
// the browser handles the keyboard and screen-reader semantics, and the
// open/closed state survives a re-render without being lifted anywhere.
const Section = ({ title, summary, children }: SectionProps) => (
    <details className="admin-integrations-section">
        <summary className="admin-integrations-section-summary">
            <span className="admin-integrations-section-title">{title}</span>
            {summary && <span className="admin-integrations-section-sub">{summary}</span>}
        </summary>
        <div className="admin-integrations-section-body">{children}</div>
    </details>
);

// Weekday slugs in display order; labels resolved via i18n day_* keys.
const CUTOFF_DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;

// Curated IANA zones for the consolidation cutoff — the warehouses and
// channels we operate in. Extend as new regions come online.
const CUTOFF_TIMEZONES = [
    "Europe/London",
    "Europe/Warsaw",
    "Europe/Berlin",
    "Europe/Paris",
    "Europe/Madrid",
    "Europe/Vilnius",
    "UTC",
];

const fmtAbsolute = (iso: string | null | undefined): string => {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
};

const ChannelCredentialsForm = ({ channel, onUpdated, disabled = false, warehouses = [] }: Props) => {
    const { t } = useTranslation();

    const [baseUrlDraft, setBaseUrlDraft] = useState(channel.baseUrl);
    const [cutoffDraft, setCutoffDraft] = useState(channel.consolidationCutoffTime ?? "");
    const [consolStats, setConsolStats] = useState<ConsolidationStats | null>(null);
    const [senderDraft, setSenderDraft] = useState(channel.notifyFromAddress);
    const [senderNameDraft, setSenderNameDraft] = useState(channel.notifyFromName);
    const [replyToDraft, setReplyToDraft] = useState(channel.notifyReplyToAddress ?? "");
    const [logoDraft, setLogoDraft] = useState(channel.logoUrl);
    const [aliasDraft, setAliasDraft] = useState("");
    const [testEmailNote, setTestEmailNote] = useState<string | null>(null);
    const [testEmailScenario, setTestEmailScenario] = useState<TestEmailScenario>("shipment");
    const [testEmailLanguage, setTestEmailLanguage] = useState<TestEmailLanguage>("en");
    const [sendingTestEmail, setSendingTestEmail] = useState(false);

    useEffect(() => {
        setCutoffDraft(channel.consolidationCutoffTime ?? "");
    }, [channel.id, channel.consolidationCutoffTime]);

    useEffect(() => {
        setSenderDraft(channel.notifyFromAddress);
        setSenderNameDraft(channel.notifyFromName);
        setReplyToDraft(channel.notifyReplyToAddress ?? "");
        setLogoDraft(channel.logoUrl);
    }, [channel.id, channel.notifyFromAddress, channel.notifyFromName, channel.notifyReplyToAddress, channel.logoUrl]);

    // Orders vs consolidated shipments for the last 30 days — loaded
    // once per card while the consolidate toggle is on.
    useEffect(() => {
        if (!channel.consolidateOrders || disabled) { setConsolStats(null); return; }
        let cancelled = false;
        (async () => {
            try {
                const res = await apiFetch(
                    `/admin/integrations/${encodeURIComponent(channel.channelCode)}/consolidation-stats?days=30`,
                );
                const json = await res.json().catch(() => ({}));
                if (!cancelled && res.ok && json?.stats) setConsolStats(json.stats);
            } catch { /* stats are decorative — never block the form */ }
        })();
        return () => { cancelled = true; };
    }, [channel.channelCode, channel.consolidateOrders, disabled]);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState<TestConnectionResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setBaseUrlDraft(channel.baseUrl);
    }, [channel.id, channel.baseUrl]);

    const patch = useCallback(
        async (body: ChannelPatch): Promise<void> => {
            if (disabled) return;
            setSaving(true);
            setError(null);
            try {
                const res = await apiFetch(
                    `/admin/integrations/${encodeURIComponent(channel.channelCode)}`,
                    {
                        method: "PATCH",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(body),
                    },
                );
                const json = await res.json().catch(() => ({}));
                if (!res.ok) {
                    throw new Error(json?.message ?? `HTTP ${res.status}`);
                }
                if (json?.channel) onUpdated(json.channel);
            } catch (err) {
                const msg = err instanceof Error ? err.message : String(err);
                setError(msg);
                throw err;
            } finally {
                setSaving(false);
            }
        },
        [channel.channelCode, onUpdated, disabled],
    );

    const toggleEnabled = async () => {
        try { await patch({ enabled: !channel.enabled }); } catch { /* surfaced */ }
    };

    const toggleConsolidateOrders = async () => {
        try { await patch({ consolidateOrders: !channel.consolidateOrders }); } catch { /* surfaced */ }
    };

    const toggleConsolidatePalletOrders = async () => {
        try { await patch({ consolidatePalletOrders: !channel.consolidatePalletOrders }); } catch { /* surfaced */ }
    };

    // Per-warehouse pallet consolidation (migration 276). The chip shows
    // where the rule APPLIES (active = consolidates), the server stores
    // the inverse — the excluded warehouses — so a warehouse added later
    // consolidates by default without anyone touching this card.
    const palletExcluded = channel.palletConsolidationExcludedWarehouses ?? [];
    const togglePalletWarehouse = async (warehouse: string) => {
        const next = palletExcluded.includes(warehouse)
            ? palletExcluded.filter((w) => w !== warehouse)
            : [...palletExcluded, warehouse];
        try { await patch({ palletConsolidationExcludedWarehouses: next }); } catch { /* surfaced */ }
    };
    // Chips for every Portal warehouse, plus any excluded name the list
    // no longer carries (renamed/removed warehouse) so it can still be
    // un-ticked rather than sitting invisible in the exclusion list.
    const palletWarehouseChips = [
        ...warehouses.map((w) => w.warehouse),
        ...palletExcluded.filter((w) => !warehouses.some((o) => o.warehouse === w)),
    ];

    const toggleAiExtraProtection = async () => {
        try { await patch({ aiExtraProtection: !channel.aiExtraProtection }); } catch { /* surfaced */ }
    };

    const toggleEnforceExtraProtection = async () => {
        try { await patch({ enforceExtraProtection: !channel.enforceExtraProtection }); } catch { /* surfaced */ }
    };

    const commitCutoffTime = async (value: string) => {
        const next = value || null;   // cleared input = no cutoff window
        if (next === channel.consolidationCutoffTime) return;
        try { await patch({ consolidationCutoffTime: next }); } catch { /* surfaced */ }
    };

    const commitCutoffTimezone = async (value: string) => {
        if (value === channel.consolidationTimezone) return;
        try { await patch({ consolidationTimezone: value }); } catch { /* surfaced */ }
    };

    const toggleCutoffDay = async (day: string) => {
        const current = channel.consolidationCutoffDays ?? [];
        const next = current.includes(day)
            ? current.filter((d) => d !== day)
            : [...current, day];
        if (next.length === 0) return;   // at least one firing day required
        try { await patch({ consolidationCutoffDays: next }); } catch { /* surfaced */ }
    };

    const commitBaseUrl = async () => {
        const trimmed = baseUrlDraft.trim();
        if (trimmed === channel.baseUrl) return;
        try { await patch({ baseUrl: trimmed }); } catch { /* surfaced */ }
    };

    const commitSender = async () => {
        const trimmed = senderDraft.trim();
        if (trimmed === channel.notifyFromAddress) return;
        try { await patch({ notifyFromAddress: trimmed }); } catch { /* surfaced */ }
    };

    const commitSenderName = async () => {
        const trimmed = senderNameDraft.trim();
        if (trimmed === channel.notifyFromName) return;
        try { await patch({ notifyFromName: trimmed }); } catch { /* surfaced */ }
    };

    const commitReplyTo = async () => {
        const trimmed = replyToDraft.trim();
        if (trimmed === (channel.notifyReplyToAddress ?? "")) return;
        try { await patch({ notifyReplyToAddress: trimmed }); } catch { /* surfaced */ }
    };

    // Kept in step with isSvgUrl in replacementEmailTemplate.js — the
    // query string is ignored because CDNs hang cache-busters off it.
    const logoIsSvg = /\.svgz?$/i.test((channel.logoUrl || "").split(/[?#]/)[0]);

    const commitLogo = async () => {
        const trimmed = logoDraft.trim();
        if (trimmed === channel.logoUrl) return;
        try { await patch({ logoUrl: trimmed }); } catch { /* surfaced */ }
    };

    // --- sales-channel aliases ----------------------------------------
    // The whole list is PATCHed each time: it is a handful of short
    // strings, and a whole-list write means the server can validate the
    // set (duplicates, another channel's alias, a channel_code) rather
    // than one entry in isolation.
    const aliases = channel.salesChannelAliases ?? [];

    const addAlias = async () => {
        const a = aliasDraft.trim();
        if (!a) return;
        if (aliases.some(x => x.toLowerCase() === a.toLowerCase())) { setAliasDraft(""); return; }
        try {
            await patch({ salesChannelAliases: [...aliases, a] });
            setAliasDraft("");
        } catch { /* surfaced */ }
    };

    const removeAlias = async (a: string) => {
        try { await patch({ salesChannelAliases: aliases.filter(x => x !== a) }); } catch { /* surfaced */ }
    };

    // Sends the REAL template — in the shape picked in the scenario
    // dropdown — to an address of the admin's choosing, so a brand's sender
    // identity can be proven to deliver, and each email's wording checked,
    // without shipping anything or touching a customer.
    const sendTestEmail = async () => {
        if (disabled || sendingTestEmail) return;
        const to = window.prompt(t("channel_test_email_prompt"), "");
        if (!to) return;
        setError(null);
        setTestEmailNote(null);
        setSendingTestEmail(true);
        try {
            const res = await apiFetch(
                `/admin/integrations/${encodeURIComponent(channel.channelCode)}/test-email`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ to, scenario: testEmailScenario, language: testEmailLanguage }),
                },
            );
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(t("channel_test_email_failed", { detail: data?.message || String(res.status) }));
                return;
            }
            setTestEmailNote(t("channel_test_email_sent", {
                from: data?.from ?? "",
                scenario: t(`channel_test_email_scenario_${testEmailScenario}`),
            }));
        } catch {
            setError(t("channel_test_email_failed", { detail: "network" }));
        } finally {
            setSendingTestEmail(false);
        }
    };

    const saveOutboundSecret = async (plaintext: string) => {
        await patch({ outboundSecret: plaintext });
    };
    const clearOutboundSecret = async () => {
        await patch({ outboundSecret: null });
    };
    const saveInboundSecret = async (plaintext: string) => {
        await patch({ inboundSecret: plaintext });
    };
    const clearInboundSecret = async () => {
        await patch({ inboundSecret: null });
    };

    const runTest = async () => {
        if (disabled) return;
        setTesting(true);
        setTestResult(null);
        setError(null);
        try {
            const res = await apiFetch(
                `/admin/integrations/${encodeURIComponent(channel.channelCode)}/test`,
                { method: "POST" },
            );
            const json = (await res.json().catch(() => ({}))) as TestConnectionResult;
            setTestResult(json);
        } catch (err) {
            setTestResult({
                ok: false,
                status: null,
                latencyMs: 0,
                error: err instanceof Error ? err.message : String(err),
            });
        } finally {
            setTesting(false);
        }
    };

    const [testingOutbound, setTestingOutbound] = useState(false);
    const [outboundResult, setOutboundResult] = useState<{ ok: boolean; status: number | null; error: string | null } | null>(null);
    const runOutboundTest = async () => {
        if (disabled) return;
        setTestingOutbound(true);
        setOutboundResult(null);
        setError(null);
        try {
            const res = await apiFetch(
                `/admin/integrations/${encodeURIComponent(channel.channelCode)}/test-outbound`,
                { method: "POST" },
            );
            const json = await res.json().catch(() => ({}));
            setOutboundResult({
                ok: !!json?.ok,
                status: json?.status ?? null,
                error: json?.error ?? null,
            });
        } catch (err) {
            setOutboundResult({
                ok: false,
                status: null,
                error: err instanceof Error ? err.message : String(err),
            });
        } finally {
            setTestingOutbound(false);
        }
    };

    // Bulk push runs async on the BE — kick it off, then poll the
    // job row every ~2s until it reaches a terminal phase. Two
    // progress signals:
    //   * job.enqueued / job.total_products   (BE work, fast)
    //   * delivery counts on outbox            (partner-side, slow)
    // The BE returns a derived `phase` + `is_terminal` so the FE
    // doesn't have to re-derive "are we done yet" from raw counts.
    type PushPhase =
        | "enqueueing" | "delivering" | "completed"
        | "completed_with_errors" | "cancelling" | "cancelled" | "failed";
    type PushJob = {
        id: number;
        total_products: number;
        enqueued: number;
        status: "running" | "enqueued" | "failed" | "cancelled";
        phase: PushPhase;
        is_terminal: boolean;
        enqueue_errors: { sku?: string; warehouse?: string; error?: string; stage?: string }[];
        delivery: {
            delivered: number;
            abandoned: number;
            pending: number;
            recent_failures: { idem_key: string; last_status_code: number | null; last_error: string | null; abandoned_at: string }[];
        };
    };
    const [pushingAll, setPushingAll] = useState(false);
    const [pushJob, setPushJob] = useState<PushJob | null>(null);
    const [pushJobError, setPushJobError] = useState<string | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);

    // Sync-product count + ETA for THIS channel. Fetched on mount and
    // whenever the channel toggles enabled, so the button tooltip and
    // the confirm modal both quote a live figure rather than a static
    // "may take hours". null = not yet loaded.
    const [syncCount, setSyncCount] = useState<{ count: number; eta_minutes: number } | null>(null);
    useEffect(() => {
        if (disabled || !channel.enabled) { setSyncCount(null); return; }
        let cancelled = false;
        (async () => {
            try {
                const res = await apiFetch(
                    `/admin/integrations/${encodeURIComponent(channel.channelCode)}/sync-product-count`,
                );
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const j = await res.json();
                if (!cancelled) setSyncCount({ count: j.count ?? 0, eta_minutes: j.eta_minutes ?? 0 });
            } catch {
                if (!cancelled) setSyncCount(null);
            }
        })();
        return () => { cancelled = true; };
    }, [channel.channelCode, channel.enabled, disabled]);

    // Poll until the job reaches a terminal phase (completed /
    // completed_with_errors / failed / cancelled). is_terminal is
    // computed BE-side from the live delivery counts, so this stops
    // exactly when the partner has received (or abandoned) everything
    // — not merely when enqueueing finished.
    useEffect(() => {
        if (!pushJob || pushJob.is_terminal) return;
        const interval = setInterval(async () => {
            try {
                const res = await apiFetch(
                    `/admin/integrations/${encodeURIComponent(channel.channelCode)}/push-jobs/${pushJob.id}`,
                );
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const j: PushJob = await res.json();
                setPushJob(j);
            } catch {
                // Transient network issues — keep the last snapshot; retry next tick.
            }
        }, 2000);
        return () => clearInterval(interval);
    }, [pushJob, channel.channelCode]);

    // Open the confirmation modal (no longer a native window.confirm).
    const openPushConfirm = () => {
        if (disabled) return;
        setConfirmOpen(true);
    };

    // Fire the actual bulk push after the operator confirms.
    const confirmPushAll = async () => {
        setConfirmOpen(false);
        setPushingAll(true);
        setPushJob(null);
        setPushJobError(null);
        setError(null);
        try {
            const res = await apiFetch(
                `/admin/integrations/${encodeURIComponent(channel.channelCode)}/push-all-stock`,
                { method: "POST" },
            );
            const json = await res.json().catch(() => ({}));
            if (!res.ok || !json?.ok) {
                setPushJobError(json?.error || json?.message || `HTTP ${res.status}`);
                return;
            }
            if (!json.job_id) {
                // Zero-products case — BE returns 200 with started:false.
                setPushJobError(json.message || t("integrations_push_all_stock_no_products"));
                return;
            }
            // Seed the polling state — minimal shape, polls fill in
            // the rest.
            setPushJob({
                id: json.job_id,
                total_products: json.total,
                enqueued: 0,
                status: "running",
                phase: "enqueueing",
                is_terminal: false,
                enqueue_errors: [],
                delivery: { delivered: 0, abandoned: 0, pending: 0, recent_failures: [] },
            });
        } catch (err) {
            setPushJobError(err instanceof Error ? err.message : String(err));
        } finally {
            setPushingAll(false);
        }
    };

    const cancelPushJob = async () => {
        if (!pushJob) return;
        try {
            await apiFetch(
                `/admin/integrations/${encodeURIComponent(channel.channelCode)}/push-jobs/${pushJob.id}/cancel`,
                { method: "POST" },
            );
            // Next poll tick will pick up the new status.
        } catch { /* ignore */ }
    };

    // ETA copy helper — "about 9 minutes" / "under a minute".
    const etaText = (mins: number | undefined): string =>
        !mins || mins <= 0
            ? t("integrations_push_eta_quick")
            : t("integrations_push_eta_minutes", { n: String(mins) });

    const inputsDisabled = disabled || saving;

    // One line of state per collapsed section, so the common reason to
    // open a panel — "what is this set to?" — usually needs no click.
    const apiSummary = [
        channel.pushMode === "live"
            ? t("integrations_push_mode_live")
            : t("integrations_push_mode_dry_run"),
        channel.baseUrl ? t("channel_section_api_configured") : t("channel_section_api_no_url"),
        channel.pushOrderStatus ? t("channel_section_api_order_status_on") : null,
        channel.channelDocuments ? t("channel_section_api_documents_on") : null,
        channel.pushExchangeOrders ? t("channel_section_api_exchange_orders_on") : null,
        channel.pushReplacementOrders ? t("channel_section_api_replacement_orders_on") : null,
    ].filter(Boolean).join(" · ");

    const emailSummary = !channel.notifyFromAddress
        ? t("channel_section_emails_no_sender")
        : [
            channel.notifyFromAddress,
            channel.notifyCustomerNonApi ? t("channel_section_emails_non_api_on") : null,
            channel.notifyCustomerApi ? t("channel_section_emails_api_on") : null,
        ].filter(Boolean).join(" · ")
            || channel.notifyFromAddress;

    const consolidationSummary = [
        channel.consolidateOrders ? t("channel_section_consolidation_parcel_on") : null,
        channel.consolidatePalletOrders
            ? (palletExcluded.length > 0
                ? t("channel_section_consolidation_pallet_on_except", { warehouses: palletExcluded.join(", ") })
                : t("channel_section_consolidation_pallet_on"))
            : null,
    ].filter(Boolean).join(" · ")
        || t("integrations_consolidate_orders_off");

    const protectionSummary = [
        channel.aiExtraProtection ? t("channel_section_protection_ai") : null,
        channel.enforceExtraProtection ? t("channel_section_protection_enforced") : null,
    ].filter(Boolean).join(" · ") || t("channel_section_protection_off");

    return (
        <div className={`admin-integrations-form${disabled ? " admin-integrations-form--disabled" : ""}`}>
            <div className="admin-integrations-form-row admin-integrations-form-row--toggle">
                <label className="admin-settings-toggle" title={t("integrations_master_enabled_hint")}>
                    <input
                        type="checkbox"
                        checked={channel.enabled}
                        onChange={toggleEnabled}
                        disabled={inputsDisabled}
                    />
                    <span className="admin-settings-slider" />
                </label>
                <span className="admin-integrations-toggle-label">
                    {t("integrations_master_enabled")}
                </span>
                {saving && <Loader2 size={14} className="admin-integrations-spinner" />}
            </div>


            <Section title={t("channel_section_api")} summary={apiSummary}>
                <div className="admin-integrations-field">
                    <label className="admin-integrations-field-label">
                        {t("integrations_push_mode")}
                    </label>
                    <p className="admin-integrations-field-hint">{t("integrations_push_mode_hint")}</p>
                    <div className="admin-integrations-mode-toggle">
                        <button
                            type="button"
                            className={`admin-integrations-mode-btn${channel.pushMode === "dry_run" ? " admin-integrations-mode-btn--active" : ""}`}
                            onClick={() => { if (channel.pushMode !== "dry_run") patch({ pushMode: "dry_run" }).catch(() => {}); }}
                            disabled={inputsDisabled}
                        >
                            {t("integrations_push_mode_dry_run")}
                        </button>
                        <button
                            type="button"
                            className={`admin-integrations-mode-btn admin-integrations-mode-btn--live${channel.pushMode === "live" ? " admin-integrations-mode-btn--active" : ""}`}
                            onClick={() => { if (channel.pushMode !== "live") patch({ pushMode: "live" }).catch(() => {}); }}
                            disabled={inputsDisabled}
                        >
                            {t("integrations_push_mode_live")}
                        </button>
                    </div>
                </div>

                {/* Parcel tracking to BargainFox's orders page (migration 267,
                    partnerStatusSync.js). Only BargainFox takes POST /order-status. */}
                {channel.vendorFamily === "bargainfox" && (
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">
                            {t("channel_order_status")}
                        </label>
                        <p className="admin-integrations-field-hint">{t("channel_order_status_hint")}</p>
                        <div className="admin-integrations-form-row admin-integrations-form-row--toggle">
                            <label className="admin-settings-toggle" title={t("channel_order_status_hint")}>
                                <input
                                    type="checkbox"
                                    checked={channel.pushOrderStatus}
                                    onChange={e => { patch({ pushOrderStatus: e.target.checked }).catch(() => {}); }}
                                    disabled={inputsDisabled}
                                />
                                <span className="admin-settings-slider" />
                            </label>
                            <span className="admin-integrations-toggle-label">
                                {channel.pushOrderStatus ? t("channel_notify_on") : t("channel_notify_off")}
                            </span>
                        </div>
                    </div>
                )}

                {/* BargainFox's own sales invoices and credit notes on the
                    Orders page order detail (order-channel-documents.tsx).
                    BargainFox generates each PDF on request; Portal proxies
                    it and never stores it. */}
                {channel.vendorFamily === "bargainfox" && (
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">
                            {t("channel_documents")}
                        </label>
                        <p className="admin-integrations-field-hint">{t("channel_documents_hint")}</p>
                        <div className="admin-integrations-form-row admin-integrations-form-row--toggle">
                            <label className="admin-settings-toggle" title={t("channel_documents_hint")}>
                                <input
                                    type="checkbox"
                                    checked={!!channel.channelDocuments}
                                    onChange={e => { patch({ channelDocuments: e.target.checked }).catch(() => {}); }}
                                    disabled={inputsDisabled}
                                    aria-label={t("channel_documents")}
                                />
                                <span className="admin-settings-slider" />
                            </label>
                            <span className="admin-integrations-toggle-label">
                                {channel.channelDocuments ? t("channel_notify_on") : t("channel_notify_off")}
                            </span>
                        </div>
                    </div>
                )}

                {/* BargainFox Returns exchange orders on the customer's BargainFox
                    orders page (JE-3352, exchangeOrderSync.js): a snapshot whenever
                    one changes. BargainFox only. */}
                {channel.vendorFamily === "bargainfox" && (
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">
                            {t("channel_exchange_orders")}
                        </label>
                        <p className="admin-integrations-field-hint">{t("channel_exchange_orders_hint")}</p>
                        <div className="admin-integrations-form-row admin-integrations-form-row--toggle">
                            <label className="admin-settings-toggle" title={t("channel_exchange_orders_hint")}>
                                <input
                                    type="checkbox"
                                    checked={!!channel.pushExchangeOrders}
                                    onChange={e => { patch({ pushExchangeOrders: e.target.checked }).catch(() => {}); }}
                                    disabled={inputsDisabled}
                                    aria-label={t("channel_exchange_orders")}
                                />
                                <span className="admin-settings-slider" />
                            </label>
                            <span className="admin-integrations-toggle-label">
                                {channel.pushExchangeOrders ? t("channel_notify_on") : t("channel_notify_off")}
                            </span>
                        </div>
                    </div>
                )}

                {/* Portal's replacement orders (REP…) on the same BargainFox orders
                    page (exchangeOrderSync.js, type "replacement"). BargainFox only. */}
                {channel.vendorFamily === "bargainfox" && (
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">
                            {t("channel_replacement_orders")}
                        </label>
                        <p className="admin-integrations-field-hint">{t("channel_replacement_orders_hint")}</p>
                        <div className="admin-integrations-form-row admin-integrations-form-row--toggle">
                            <label className="admin-settings-toggle" title={t("channel_replacement_orders_hint")}>
                                <input
                                    type="checkbox"
                                    checked={!!channel.pushReplacementOrders}
                                    onChange={e => { patch({ pushReplacementOrders: e.target.checked }).catch(() => {}); }}
                                    disabled={inputsDisabled}
                                    aria-label={t("channel_replacement_orders")}
                                />
                                <span className="admin-settings-slider" />
                            </label>
                            <span className="admin-integrations-toggle-label">
                                {channel.pushReplacementOrders ? t("channel_notify_on") : t("channel_notify_off")}
                            </span>
                        </div>
                    </div>
                )}

                <div className="admin-integrations-field">
                    <label className="admin-integrations-field-label">
                        {t("integrations_field_base_url")}
                    </label>
                    <p className="admin-integrations-field-hint">{t("integrations_field_base_url_hint")}</p>
                    <input
                        type="url"
                        className="admin-integrations-input"
                        value={baseUrlDraft}
                        onChange={e => setBaseUrlDraft(e.target.value)}
                        onBlur={commitBaseUrl}
                        placeholder={t("integrations_field_base_url_placeholder")}
                        disabled={inputsDisabled}
                    />
                </div>

                {/* Customer-email branding. Used by the replacement-shipped
                    notification, which is the ONLY customer-facing email
                    Portal sends — replacement orders never notify this
                    channel's API, so this is how the customer hears about
                    them. A blank sender address switches it off entirely
                    rather than falling back to another brand. */}
                <SecretField
                    label={t("integrations_field_outbound_secret")}
                    hint={t("integrations_field_outbound_secret_hint")}
                    secret={channel.outboundSecret}
                    onSave={saveOutboundSecret}
                    onClear={clearOutboundSecret}
                    disabled={disabled}
                />

                <SecretField
                    label={t("integrations_field_inbound_secret")}
                    hint={t("integrations_field_inbound_secret_hint")}
                    secret={channel.inboundSecret}
                    onSave={saveInboundSecret}
                    onClear={clearInboundSecret}
                    disabled={disabled}
                />

                <div className="admin-integrations-status-grid">
                    <div className="admin-integrations-status-cell">
                        <span className="admin-integrations-status-label">
                            {t("integrations_status_last_push_ok")}
                        </span>
                        <span className="admin-integrations-status-value">
                            {channel.status.lastPushOkAt
                                ? fmtAbsolute(channel.status.lastPushOkAt)
                                : t("integrations_status_never")}
                        </span>
                    </div>
                    <div className="admin-integrations-status-cell">
                        <span className="admin-integrations-status-label">
                            {t("integrations_status_last_push_failed")}
                        </span>
                        <span className="admin-integrations-status-value">
                            {channel.status.lastPushFailedAt
                                ? fmtAbsolute(channel.status.lastPushFailedAt)
                                : t("integrations_status_never")}
                        </span>
                    </div>
                    <div className="admin-integrations-status-cell">
                        <span className="admin-integrations-status-label">
                            {t("integrations_status_last_health")}
                        </span>
                        <span className="admin-integrations-status-value">
                            {channel.status.lastHealthAt ? (
                                <>
                                    {fmtAbsolute(channel.status.lastHealthAt)}
                                    {channel.status.lastHealthStatus && (
                                        <>
                                            &nbsp;·&nbsp;
                                            <span
                                                className={
                                                    channel.status.lastHealthStatus === "ok"
                                                        ? "admin-integrations-status-pill admin-integrations-status-pill--ok"
                                                        : "admin-integrations-status-pill admin-integrations-status-pill--down"
                                                }
                                            >
                                                {channel.status.lastHealthStatus === "ok"
                                                    ? t("integrations_status_ok")
                                                    : t("integrations_status_down")}
                                            </span>
                                        </>
                                    )}
                                </>
                            ) : (
                                t("integrations_status_never")
                            )}
                        </span>
                    </div>
                </div>

                <div className="admin-integrations-test-row">
                    <button
                        type="button"
                        className="btn btn-sm"
                        onClick={runTest}
                        disabled={disabled || testing || !channel.baseUrl}
                    >
                        {testing ? (
                            <>
                                <Loader2 size={14} className="admin-integrations-spinner" />{" "}
                                {t("integrations_test_running")}
                            </>
                        ) : (
                            <>
                                <Wifi size={14} /> {t("integrations_test_connection")}
                            </>
                        )}
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm"
                        onClick={runOutboundTest}
                        disabled={disabled || testingOutbound || !channel.baseUrl || !channel.outboundSecret.set}
                        title={t("integrations_test_outbound_hint")}
                    >
                        {testingOutbound ? (
                            <>
                                <Loader2 size={14} className="admin-integrations-spinner" />{" "}
                                {t("integrations_test_running")}
                            </>
                        ) : (
                            <>
                                <Wifi size={14} /> {t("integrations_test_outbound")}
                            </>
                        )}
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm"
                        onClick={openPushConfirm}
                        disabled={disabled || pushingAll || !channel.enabled || !channel.outboundSecret.set}
                        title={
                            syncCount
                                ? t("integrations_push_all_stock_hint_counted", {
                                    count: syncCount.count.toLocaleString(),
                                    eta: etaText(syncCount.eta_minutes),
                                })
                                : t("integrations_push_all_stock_hint")
                        }
                    >
                        {pushingAll ? (
                            <>
                                <Loader2 size={14} className="admin-integrations-spinner" />{" "}
                                {t("integrations_push_all_stock_running")}
                            </>
                        ) : (
                            <>{t("integrations_push_all_stock")}</>
                        )}
                    </button>
                    {outboundResult && (
                        <span
                            className={
                                outboundResult.ok
                                    ? "admin-integrations-test-result admin-integrations-test-result--ok"
                                    : "admin-integrations-test-result admin-integrations-test-result--fail"
                            }
                        >
                            {outboundResult.ok ? (
                                t("integrations_test_outbound_success", {
                                    status: String(outboundResult.status ?? "?"),
                                })
                            ) : (
                                <>
                                    <WifiOff size={14} />{" "}
                                    {t("integrations_test_outbound_failure", {
                                        reason: outboundResult.error ?? `HTTP ${outboundResult.status ?? "?"}`,
                                    })}
                                </>
                            )}
                        </span>
                    )}
                    {pushJobError && (
                        <span className="admin-integrations-test-result admin-integrations-test-result--fail">
                            {t("integrations_push_all_stock_failure", { reason: pushJobError })}
                        </span>
                    )}
                </div>
                {pushJob && (
                    <div className="admin-integrations-push-job">
                        <div className="admin-integrations-push-job-head">
                            <span className="admin-integrations-push-job-label">
                                {t("integrations_push_all_stock_progress_label", {
                                    channel: channel.displayName,
                                })}
                            </span>
                            <span className={`admin-integrations-push-job-status admin-integrations-push-job-status--${pushJob.phase}`}>
                                {t(`integrations_push_phase_${pushJob.phase}`)}
                            </span>
                            {/* Cancel is only meaningful while we're still
                                enqueueing new rows; once everything's queued
                                there's nothing left to stop. */}
                            {pushJob.phase === "enqueueing" && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-ghost"
                                    onClick={cancelPushJob}
                                >
                                    {t("integrations_push_job_cancel")}
                                </button>
                            )}
                        </div>
                        {/* Terminal completion banner — the clear "it's done"
                            signal the operator was missing. */}
                        {pushJob.phase === "completed" && (
                            <div className="admin-integrations-push-job-done admin-integrations-push-job-done--ok">
                                {t("integrations_push_done_ok", {
                                    n: pushJob.delivery.delivered.toLocaleString(),
                                })}
                            </div>
                        )}
                        {pushJob.phase === "completed_with_errors" && (
                            <div className="admin-integrations-push-job-done admin-integrations-push-job-done--warn">
                                {t("integrations_push_done_errors", {
                                    delivered: pushJob.delivery.delivered.toLocaleString(),
                                    failed: pushJob.delivery.abandoned.toLocaleString(),
                                })}
                            </div>
                        )}
                        {/* Two stacked progress bars — enqueue (fast, server-side
                            work) then delivery (slow, partner round-trips). */}
                        <div className="admin-integrations-progress-row">
                            <span className="admin-integrations-progress-label">
                                {t("integrations_push_progress_queued")}
                            </span>
                            <progress
                                className="admin-integrations-progress"
                                value={pushJob.enqueued}
                                max={pushJob.total_products}
                            />
                            <span className="admin-integrations-progress-count">
                                {pushJob.enqueued.toLocaleString()} / {pushJob.total_products.toLocaleString()}
                            </span>
                        </div>
                        <div className="admin-integrations-progress-row">
                            <span className="admin-integrations-progress-label">
                                {t("integrations_push_progress_delivered")}
                            </span>
                            <progress
                                className="admin-integrations-progress"
                                value={pushJob.delivery.delivered}
                                max={Math.max(pushJob.enqueued, pushJob.delivery.delivered + pushJob.delivery.pending + pushJob.delivery.abandoned, 1)}
                            />
                            <span className="admin-integrations-progress-count">
                                {pushJob.delivery.delivered.toLocaleString()}
                                {pushJob.delivery.pending > 0 && (
                                    <> · {t("integrations_push_progress_pending", { n: pushJob.delivery.pending.toLocaleString() })}</>
                                )}
                                {pushJob.delivery.abandoned > 0 && (
                                    <> · <span className="admin-integrations-push-job-fail">
                                        {t("integrations_push_progress_failed", { n: pushJob.delivery.abandoned.toLocaleString() })}
                                    </span></>
                                )}
                            </span>
                        </div>
                        {(pushJob.enqueue_errors.length > 0 || pushJob.delivery.recent_failures.length > 0) && (
                            <details className="admin-integrations-push-job-errors">
                                <summary>
                                    {t("integrations_push_job_errors_summary", {
                                        n: String(pushJob.enqueue_errors.length + pushJob.delivery.recent_failures.length),
                                    })}
                                </summary>
                                <ul className="admin-integrations-push-job-errors-list">
                                    {pushJob.enqueue_errors.map((e, i) => (
                                        <li key={`enq-${i}`}>
                                            <strong>{e.sku ?? e.stage ?? t("integrations_push_job_unknown")}</strong>
                                            {e.warehouse ? ` (${e.warehouse})` : ""}: {e.error}
                                        </li>
                                    ))}
                                    {pushJob.delivery.recent_failures.map((f) => (
                                        <li key={f.idem_key}>
                                            <strong>{f.idem_key}</strong>: HTTP {f.last_status_code ?? "?"} — {f.last_error}
                                        </li>
                                    ))}
                                </ul>
                            </details>
                        )}
                    </div>
                )}
                <div>
                    {testResult && (
                        <span
                            className={
                                testResult.ok
                                    ? "admin-integrations-test-result admin-integrations-test-result--ok"
                                    : "admin-integrations-test-result admin-integrations-test-result--fail"
                            }
                        >
                            {testResult.ok ? (
                                t("integrations_test_success", { latencyMs: String(testResult.latencyMs) })
                            ) : (
                                <>
                                    <WifiOff size={14} />{" "}
                                    {t("integrations_test_failure", {
                                        reason: testResult.error ?? `HTTP ${testResult.status ?? "?"}`,
                                    })}
                                </>
                            )}
                        </span>
                    )}
                </div>
            </Section>

            <Section title={t("channel_section_emails")} summary={emailSummary}>
                <div className="admin-integrations-field">
                    <label className="admin-integrations-field-label">
                        {t("channel_email_branding")}
                    </label>
                    <p className="admin-integrations-field-hint">{t("channel_email_branding_hint")}</p>
                    <div className="admin-integrations-form-row">
                        <input
                            type="email"
                            className="admin-integrations-input"
                            value={senderDraft}
                            onChange={e => setSenderDraft(e.target.value)}
                            onBlur={commitSender}
                            placeholder={t("channel_sender_address")}
                            aria-label={t("channel_sender_address")}
                            disabled={inputsDisabled}
                        />
                        <input
                            type="text"
                            className="admin-integrations-input"
                            value={senderNameDraft}
                            onChange={e => setSenderNameDraft(e.target.value)}
                            onBlur={commitSenderName}
                            placeholder={t("channel_sender_name")}
                            aria-label={t("channel_sender_name")}
                            disabled={inputsDisabled}
                        />
                    </div>
                    <p className="admin-integrations-field-hint">{t("channel_sender_blank_hint")}</p>

                    {/* Reply-To. Optional: blank sends no header, so a
                        reply goes wherever the From address takes it. */}
                    <div className="admin-integrations-form-row">
                        <input
                            type="email"
                            className="admin-integrations-input"
                            value={replyToDraft}
                            onChange={e => setReplyToDraft(e.target.value)}
                            onBlur={commitReplyTo}
                            placeholder={t("channel_reply_to_address")}
                            aria-label={t("channel_reply_to_address")}
                            disabled={inputsDisabled}
                        />
                    </div>
                    <p className="admin-integrations-field-hint">{t("channel_reply_to_hint")}</p>

                    {/* The logo is a pasted URL, not an upload. An uploaded
                        image would have to be served from a route that is both
                        unauthenticated and IP-allow-list exempt, because the
                        thing fetching it is the customer's email client. */}
                    <div className="admin-integrations-logo-row">
                        <input
                            type="url"
                            className="admin-integrations-input"
                            value={logoDraft}
                            onChange={e => setLogoDraft(e.target.value)}
                            onBlur={commitLogo}
                            placeholder="https://example.com/logo.png"
                            aria-label={t("channel_logo")}
                            disabled={inputsDisabled}
                        />
                        {channel.logoUrl && !logoIsSvg && (
                            <img
                                className="admin-integrations-logo-preview"
                                src={channel.logoUrl}
                                alt={channel.notifyFromName || channel.displayName}
                                // A dead URL must not leave a broken-image icon
                                // sitting in the settings panel.
                                onError={e => { e.currentTarget.style.display = "none"; }}
                            />
                        )}
                    </div>
                    <p className="admin-integrations-field-hint">{t("channel_logo_hint")}</p>

                    {/* Which of the customer emails to sample. Every shape
                        the template can take is listed, so the partial and
                        consolidated wording can be checked in a real inbox
                        without waiting for such an order to ship. */}
                    <div className="admin-integrations-form-row admin-integrations-test-email-row">
                        <select
                            className="admin-integrations-input admin-integrations-input--scenario"
                            value={testEmailScenario}
                            onChange={e => setTestEmailScenario(e.target.value as TestEmailScenario)}
                            aria-label={t("channel_test_email_scenario")}
                            disabled={inputsDisabled || !channel.notifyFromAddress}
                        >
                            {TEST_EMAIL_SCENARIOS.map(id => (
                                <option key={id} value={id}>{t(`channel_test_email_scenario_${id}`)}</option>
                            ))}
                        </select>
                        {/* The copy language. A real Jobalots email uses the
                            buyer's own choice from their Jobalots account. */}
                        <select
                            className="admin-integrations-input admin-integrations-input--language"
                            value={testEmailLanguage}
                            onChange={e => setTestEmailLanguage(e.target.value as TestEmailLanguage)}
                            aria-label={t("channel_test_email_language")}
                            title={t("channel_test_email_language")}
                            disabled={inputsDisabled || !channel.notifyFromAddress}
                        >
                            {TEST_EMAIL_LANGUAGES.map(l => (
                                <option key={l.code} value={l.code}>{l.name}</option>
                            ))}
                        </select>
                        <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={sendTestEmail}
                            disabled={inputsDisabled || !channel.notifyFromAddress || sendingTestEmail}
                            title={t("channel_test_email_hint")}
                        >
                            {sendingTestEmail ? <Loader2 size={14} className="admin-integrations-spinner" /> : <Mail size={14} />}
                            {t("channel_test_email")}
                        </button>
                    </div>
                    <p className="admin-integrations-field-hint">{t("channel_test_email_hint")}</p>
                    {/* A stored SVG predates the PATCH validation that now
                        refuses one. Warning INSTEAD of the preview is the
                        point: rendered here it looks perfect, which is exactly
                        what convinced someone it was fine — the email drops it
                        and falls back to the brand name. */}
                    {logoIsSvg && (
                        <p className="admin-integrations-field-hint admin-integrations-logo-warning">
                            {t("channel_logo_svg_warning")}
                        </p>
                    )}
                    {testEmailNote && (
                        <p className="admin-integrations-field-hint admin-integrations-test-ok">{testEmailNote}</p>
                    )}
                </div>


                <div className="admin-integrations-field">
                    <label className="admin-integrations-field-label">
                        {t("channel_notify_non_api")}
                    </label>
                    <p className="admin-integrations-field-hint">{t("channel_notify_non_api_hint")}</p>
                    <div className="admin-integrations-form-row admin-integrations-form-row--toggle">
                        <label className="admin-settings-toggle" title={t("channel_notify_non_api_hint")}>
                            <input
                                type="checkbox"
                                checked={channel.notifyCustomerNonApi}
                                onChange={e => { patch({ notifyCustomerNonApi: e.target.checked }).catch(() => {}); }}
                                disabled={inputsDisabled || !channel.notifyFromAddress}
                            />
                            <span className="admin-settings-slider" />
                        </label>
                        <span className="admin-integrations-toggle-label">
                            {channel.notifyCustomerNonApi ? t("channel_notify_on") : t("channel_notify_off")}
                        </span>
                    </div>
                </div>

                <div className="admin-integrations-field">
                    <label className="admin-integrations-field-label">
                        {t("channel_notify_api")}
                    </label>
                    <p className="admin-integrations-field-hint">{t("channel_notify_api_hint")}</p>
                    <div className="admin-integrations-form-row admin-integrations-form-row--toggle">
                        <label className="admin-settings-toggle" title={t("channel_notify_api_hint")}>
                            <input
                                type="checkbox"
                                checked={channel.notifyCustomerApi}
                                onChange={e => { patch({ notifyCustomerApi: e.target.checked }).catch(() => {}); }}
                                disabled={inputsDisabled || !channel.notifyFromAddress}
                            />
                            <span className="admin-settings-slider" />
                        </label>
                        <span className="admin-integrations-toggle-label">
                            {channel.notifyCustomerApi ? t("channel_notify_on") : t("channel_notify_off")}
                        </span>
                    </div>
                    {/* Switching this on while the partner still sends
                        their own dispatch mail means the buyer gets two of
                        them. Said at the control, not in a release note. */}
                    {channel.notifyCustomerApi && (
                        <p className="admin-integrations-field-hint admin-integrations-logo-warning">
                            {t("channel_notify_api_warning")}
                        </p>
                    )}
                </div>

                {/* Both toggles are inert without a sender address: the
                    delivery adapter refuses to borrow another brand's. */}
                {!channel.notifyFromAddress && (
                    <p className="admin-integrations-field-hint admin-integrations-logo-warning">
                        {t("channel_notify_needs_sender")}
                    </p>
                )}

                <div className="admin-integrations-field">
                    <label className="admin-integrations-field-label">
                        {t("channel_aliases")}
                    </label>
                    <p className="admin-integrations-field-hint">{t("channel_aliases_hint")}</p>
                    <div className="admin-integrations-alias-list">
                        {aliases.map(a => (
                            <span key={a} className="admin-integrations-alias-chip">
                                {a}
                                <button
                                    type="button"
                                    className="admin-integrations-alias-remove"
                                    onClick={() => removeAlias(a)}
                                    disabled={inputsDisabled}
                                    aria-label={t("channel_aliases_remove", { alias: a })}
                                    title={t("channel_aliases_remove", { alias: a })}
                                >
                                    <X size={12} />
                                </button>
                            </span>
                        ))}
                        {aliases.length === 0 && (
                            <span className="admin-integrations-alias-empty">
                                {t("channel_aliases_empty", { code: channel.channelCode })}
                            </span>
                        )}
                    </div>
                    <div className="admin-integrations-alias-add">
                        <input
                            type="text"
                            className="admin-integrations-input"
                            value={aliasDraft}
                            onChange={e => setAliasDraft(e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addAlias(); } }}
                            placeholder={t("channel_aliases_placeholder")}
                            aria-label={t("channel_aliases")}
                            disabled={inputsDisabled}
                        />
                        <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={addAlias}
                            disabled={inputsDisabled || !aliasDraft.trim()}
                        >
                            <Plus size={14} /> {t("channel_aliases_add")}
                        </button>
                    </div>
                </div>

            </Section>

            <Section title={t("channel_section_consolidation")} summary={consolidationSummary}>
                <div className="admin-integrations-field">
                    <label className="admin-integrations-field-label">
                        {t("integrations_consolidate_orders")}
                    </label>
                    <p className="admin-integrations-field-hint">{t("integrations_consolidate_orders_hint")}</p>
                    <div className="admin-integrations-form-row admin-integrations-form-row--toggle">
                        <label className="admin-settings-toggle" title={t("integrations_consolidate_orders_hint")}>
                            <input
                                type="checkbox"
                                checked={channel.consolidateOrders}
                                onChange={toggleConsolidateOrders}
                                disabled={inputsDisabled}
                            />
                            <span className="admin-settings-slider" />
                        </label>
                        <span className="admin-integrations-toggle-label">
                            {channel.consolidateOrders
                                ? t("integrations_consolidate_orders_on")
                                : t("integrations_consolidate_orders_off")}
                        </span>
                    </div>
                    {channel.consolidateOrders && (
                        <div className="admin-integrations-form-row admin-integrations-form-row--cutoff">
                            <label className="admin-integrations-inline-field">
                                <span className="admin-integrations-inline-label">
                                    {t("integrations_consolidation_cutoff")}
                                </span>
                                <input
                                    type="time"
                                    className="admin-integrations-input admin-integrations-input--time"
                                    value={cutoffDraft}
                                    onChange={(e) => setCutoffDraft(e.target.value)}
                                    onBlur={() => commitCutoffTime(cutoffDraft)}
                                    disabled={inputsDisabled}
                                />
                            </label>
                            <label className="admin-integrations-inline-field">
                                <span className="admin-integrations-inline-label">
                                    {t("integrations_consolidation_timezone")}
                                </span>
                                <select
                                    className="admin-integrations-input admin-integrations-input--timezone"
                                    value={channel.consolidationTimezone || "Europe/London"}
                                    onChange={(e) => commitCutoffTimezone(e.target.value)}
                                    disabled={inputsDisabled}
                                >
                                    {CUTOFF_TIMEZONES.map((tz) => (
                                        <option key={tz} value={tz}>{tz}</option>
                                    ))}
                                </select>
                            </label>
                            <div className="admin-integrations-inline-field admin-integrations-cutoff-days">
                                <span className="admin-integrations-inline-label">
                                    {t("integrations_consolidation_days")}
                                </span>
                                <div className="admin-integrations-day-chips">
                                    {CUTOFF_DAYS.map((day) => {
                                        const active = (channel.consolidationCutoffDays ?? []).includes(day);
                                        return (
                                            <button
                                                key={day}
                                                type="button"
                                                className={`admin-integrations-day-chip${active ? " admin-integrations-day-chip--active" : ""}`}
                                                onClick={() => toggleCutoffDay(day)}
                                                disabled={inputsDisabled || !cutoffDraft}
                                                title={t("integrations_consolidation_days_hint")}
                                            >
                                                {t(`day_short_${day}`)}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            <p className="admin-integrations-field-hint">
                                {cutoffDraft
                                    ? t("integrations_consolidation_cutoff_hint")
                                    : t("integrations_consolidation_cutoff_none")}
                            </p>
                            {consolStats && consolStats.totalOrders > 0 && (
                                <p className="admin-integrations-consol-stats">
                                    {t("integrations_consolidation_stats", {
                                        days: String(consolStats.days),
                                        orders: consolStats.totalOrders.toLocaleString(),
                                        shipments: consolStats.shipments.toLocaleString(),
                                        saved: consolStats.parcelsSaved.toLocaleString(),
                                        pct: String(consolStats.pctSaved),
                                    })}
                                </p>
                            )}
                        </div>
                    )}
                </div>

                <div className="admin-integrations-field">
                    <label className="admin-integrations-field-label">
                        {t("integrations_consolidate_pallet_orders")}
                    </label>
                    <p className="admin-integrations-field-hint">{t("integrations_consolidate_pallet_orders_hint")}</p>
                    <div className="admin-integrations-form-row admin-integrations-form-row--toggle">
                        <label className="admin-settings-toggle" title={t("integrations_consolidate_pallet_orders_hint")}>
                            <input
                                type="checkbox"
                                checked={channel.consolidatePalletOrders}
                                onChange={toggleConsolidatePalletOrders}
                                disabled={inputsDisabled}
                            />
                            <span className="admin-settings-slider" />
                        </label>
                        <span className="admin-integrations-toggle-label">
                            {channel.consolidatePalletOrders
                                ? t("integrations_consolidate_pallet_orders_on")
                                : t("integrations_consolidate_pallet_orders_off")}
                        </span>
                    </div>
                    {palletWarehouseChips.length > 0 && (
                        <div className="admin-integrations-form-row">
                            <div className="admin-integrations-inline-field admin-integrations-cutoff-days">
                                <span className="admin-integrations-inline-label">
                                    {t("integrations_consolidate_pallet_warehouses")}
                                </span>
                                <div className="admin-integrations-day-chips">
                                    {palletWarehouseChips.map((warehouse) => {
                                        const active = !palletExcluded.includes(warehouse);
                                        return (
                                            <button
                                                key={warehouse}
                                                type="button"
                                                className={`admin-integrations-day-chip${active ? " admin-integrations-day-chip--active" : ""}`}
                                                onClick={() => togglePalletWarehouse(warehouse)}
                                                disabled={inputsDisabled || !channel.consolidatePalletOrders}
                                                aria-pressed={active}
                                                title={t("integrations_consolidate_pallet_warehouses_hint")}
                                            >
                                                {warehouse}
                                            </button>
                                        );
                                    })}
                                </div>
                                <p className="admin-integrations-field-hint">
                                    {t("integrations_consolidate_pallet_warehouses_hint")}
                                </p>
                            </div>
                        </div>
                    )}
                </div>

            </Section>

            <Section title={t("channel_section_protection")} summary={protectionSummary}>
                <div className="admin-integrations-field">
                    <label className="admin-integrations-field-label">
                        {t("integrations_ai_extra_protection")}
                    </label>
                    <p className="admin-integrations-field-hint">{t("integrations_ai_extra_protection_hint")}</p>
                    <div className="admin-integrations-form-row admin-integrations-form-row--toggle">
                        <label className="admin-settings-toggle" title={t("integrations_ai_extra_protection_hint")}>
                            <input
                                type="checkbox"
                                checked={channel.aiExtraProtection}
                                onChange={toggleAiExtraProtection}
                                disabled={inputsDisabled}
                            />
                            <span className="admin-settings-slider" />
                        </label>
                        <span className="admin-integrations-toggle-label">
                            {channel.aiExtraProtection
                                ? t("integrations_ai_extra_protection_on")
                                : t("integrations_ai_extra_protection_off")}
                        </span>
                    </div>
                </div>

                {/* Enforcement is deliberately separate from assessment above:
                    switching classification off should not silently stop the
                    pack desk honouring flags operators set by hand. */}
                <div className="admin-integrations-field">
                    <label className="admin-integrations-field-label">
                        {t("integrations_enforce_extra_protection")}
                    </label>
                    <p className="admin-integrations-field-hint">{t("integrations_enforce_extra_protection_hint")}</p>
                    <div className="admin-integrations-form-row admin-integrations-form-row--toggle">
                        <label className="admin-settings-toggle" title={t("integrations_enforce_extra_protection_hint")}>
                            <input
                                type="checkbox"
                                checked={channel.enforceExtraProtection}
                                onChange={toggleEnforceExtraProtection}
                                disabled={inputsDisabled}
                            />
                            <span className="admin-settings-slider" />
                        </label>
                        <span className="admin-integrations-toggle-label">
                            {channel.enforceExtraProtection
                                ? t("integrations_enforce_extra_protection_on")
                                : t("integrations_enforce_extra_protection_off")}
                        </span>
                    </div>
                </div>

            </Section>


            {error && <p className="admin-integrations-field-error">{error}</p>}

            {/* Push-all confirmation modal. Replaces the old native
                window.confirm so we can show the live product count +
                a calculated ETA before the operator commits. */}
            {confirmOpen && (
                <div
                    className="modal-overlay"
                    onClick={() => setConfirmOpen(false)}
                    role="presentation"
                >
                    <div
                        className="modal-card admin-integrations-confirm"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="push-all-confirm-title"
                    >
                        <h3 id="push-all-confirm-title" className="admin-integrations-confirm-title">
                            {t("integrations_push_all_stock")}
                        </h3>
                        <p className="admin-integrations-confirm-body">
                            {t("integrations_push_all_stock_confirm", { channel: channel.displayName })}
                        </p>
                        <p className="admin-integrations-confirm-detail">
                            {syncCount === null
                                ? t("integrations_push_confirm_counting")
                                : syncCount.count === 0
                                    ? t("integrations_push_all_stock_no_products")
                                    : t("integrations_push_confirm_detail", {
                                        count: syncCount.count.toLocaleString(),
                                        eta: etaText(syncCount.eta_minutes),
                                    })}
                        </p>
                        <div className="admin-integrations-confirm-actions">
                            <button
                                type="button"
                                className="btn btn-sm btn-ghost"
                                onClick={() => setConfirmOpen(false)}
                            >
                                {t("cancel")}
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={confirmPushAll}
                                disabled={syncCount !== null && syncCount.count === 0}
                            >
                                {t("integrations_push_confirm_go")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChannelCredentialsForm;
